import {
  isInputRecord,
  isSameLeafValue,
  matchRowMoves,
  metadataPathKey,
  type PriceModelInputMetadataEntry,
  rebaseSchemaPathsForReorder,
} from "@forgepoint/shared";
import {
  isJSONObject,
  type JSONObject,
  type JSONValue,
} from "@/components/jsonSchemaForm/types";

/**
 * Indexes metadata entries by their schema path key for O(1) field lookup,
 * moving entries along with any array rows the user has only reordered so a
 * dragged row keeps its own provenance before the next save. Entries whose row
 * is gone are dropped.
 * @param entries - The item's price model input metadata
 * @param baselineInputs - The inputs the entries were written against
 * @param inputs - The inputs currently on the form
 * @returns A map of schema path key to entry
 */
export function indexMetadataByPath(
  entries: PriceModelInputMetadataEntry[],
  baselineInputs: JSONValue,
  inputs: JSONValue,
): Map<string, PriceModelInputMetadataEntry> {
  const rebasedPaths = rebaseSchemaPathsForReorder(
    entries.map((entry) => entry.schemaPath),
    baselineInputs,
    inputs,
  );
  return new Map<string, PriceModelInputMetadataEntry>(
    entries.flatMap((entry, index) => {
      const schemaPath = rebasedPaths[index];
      if (!schemaPath) {
        return [];
      }
      return [[metadataPathKey(schemaPath), { ...entry, schemaPath }]];
    }),
  );
}

/**
 * Reorders the baseline's array rows to sit where they now do on the form, so
 * that comparing the two by position reports a drag as no change at all.
 * @param baselineInputs - The inputs the form started from
 * @param inputs - The inputs currently on the form
 * @returns The baseline with its moved rows realigned
 */
export function alignBaselineRows(
  baselineInputs: JSONValue,
  inputs: JSONValue,
): JSONValue {
  if (!isJSONObject(baselineInputs) || !isJSONObject(inputs)) {
    return baselineInputs;
  }
  return Object.entries(baselineInputs).reduce<JSONObject>(
    (aligned, [field, value]) => {
      const moves = matchRowMoves(value, inputs[field]);
      if (!moves || !Array.isArray(value)) {
        return aligned;
      }
      const rows: JSONValue[] = [];
      moves.forEach((nextIndex, previousIndex) => {
        rows[Number(nextIndex)] = value[Number(previousIndex)];
      });
      return { ...aligned, [field]: rows };
    },
    baselineInputs,
  );
}

/**
 * Collects the leaf paths whose values differ between two form data snapshots.
 * @param previous - The form data before the change
 * @param next - The form data after the change
 * @param basePath - Path prefix of the values being compared
 * @returns Every leaf path whose value changed
 */
export function collectChangedLeafPaths(
  previous: unknown,
  next: unknown,
  basePath: string[] = [],
): string[][] {
  if (isInputRecord(previous) && isInputRecord(next)) {
    const keys = new Set([...Object.keys(previous), ...Object.keys(next)]);
    return Array.from(keys).flatMap((key) =>
      collectChangedLeafPaths(previous[key], next[key], [...basePath, key]),
    );
  }

  if (Array.isArray(previous) && Array.isArray(next)) {
    const length = Math.max(previous.length, next.length);
    return Array.from({ length }, (_unused, index) => index).flatMap((index) =>
      collectChangedLeafPaths(previous[index], next[index], [
        ...basePath,
        String(index),
      ]),
    );
  }

  if (basePath.length === 0 || isSameLeafValue(previous, next)) {
    return [];
  }
  return [basePath];
}

/**
 * Reads the value a schema path addresses, walking numeric segments as array
 * indices.
 * @param inputs - The inputs to walk
 * @param schemaPath - The path segments addressing the value
 * @returns The addressed value, or undefined when the path does not resolve
 */
export function readValueAtPath(
  inputs: JSONValue,
  schemaPath: string[],
): JSONValue {
  return schemaPath.reduce<JSONValue>((current, segment) => {
    if (Array.isArray(current)) {
      return current[Number(segment)];
    }
    return isJSONObject(current) ? current[segment] : undefined;
  }, inputs);
}

/**
 * Writes a value at a schema path without mutating the inputs it is given. The
 * path must address a field currently on the form, so every container it walks
 * through already exists.
 * @param inputs - The inputs to write into
 * @param schemaPath - The path segments addressing the leaf to write
 * @param value - The value to write at that path
 * @returns A copy of the inputs carrying the value at that path
 */
export function setValueAtPath(
  inputs: JSONValue,
  schemaPath: string[],
  value: JSONValue,
): JSONValue {
  const [segment, ...rest] = schemaPath;
  if (segment === undefined) {
    return value;
  }

  if (Array.isArray(inputs)) {
    const elements = [...inputs];
    const index = Number(segment);
    elements[index] = setValueAtPath(elements[index], rest, value);
    return elements;
  }

  const record = isJSONObject(inputs) ? inputs : {};
  return {
    ...record,
    [segment]: setValueAtPath(record[segment], rest, value),
  };
}
